import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Layout Components (always loaded — present on every page)
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import LiveBanner from './components/LiveBanner';
import CompetitionBanner from './components/CompetitionBanner';

// Lazy-loaded Page Components
const HomePage = React.lazy(() => import('./pages/HomePage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = React.lazy(() => import('./pages/VerifyEmailPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const CoursesPage = React.lazy(() => import('./pages/CoursesPage'));
const CourseDetailPage = React.lazy(() => import('./pages/CourseDetailPage'));
const CheckoutPage = React.lazy(() => import('./pages/CheckoutPage'));
const LessonPage = React.lazy(() => import('./pages/LessonPage'));
const CourseCompletionPage = React.lazy(() => import('./pages/CourseCompletionPage'));
const WorkshopsPage = React.lazy(() => import('./pages/WorkshopsPage'));
const WorkshopDetailPage = React.lazy(() => import('./pages/WorkshopDetailPage'));
const WorkshopRegistrationForm = React.lazy(() => import('./components/WorkshopRegistrationForm'));
const WorkshopCheckoutPage = React.lazy(() => import('./pages/WorkshopCheckoutPage'));
const WorkshopPaymentPage = React.lazy(() => import('./pages/WorkshopPaymentPage'));
const PortfolioPage = React.lazy(() => import('./pages/PortfolioPage'));
const NewsletterPage = React.lazy(() => import('./pages/NewsletterPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const LivePage = React.lazy(() => import('./pages/LivePage'));

// Competition Pages
const CompetitionsPage = React.lazy(() => import('./pages/CompetitionsPage'));
const CompetitionCheckoutPage = React.lazy(() => import('./pages/CompetitionCheckoutPage'));
const CompetitionFeedbackPage = React.lazy(() => import('./pages/CompetitionFeedbackPage'));

// Admin Pages
const AdminDashboardPage = React.lazy(() => import('./pages/admin/AdminDashboardPage'));
const AdminAnalyticsPage = React.lazy(() => import('./pages/admin/AdminAnalyticsPage'));
const AdminUsersPage = React.lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminUserDetailPage = React.lazy(() => import('./pages/admin/AdminUserDetailPage'));
const AdminCompetitionsPage = React.lazy(() => import('./pages/admin/AdminCompetitionsPage'));
const AdminCompetitionDetailPage = React.lazy(() => import('./pages/admin/AdminCompetitionDetailPage'));
const AdminCompetitionEditPage = React.lazy(() => import('./pages/admin/AdminCompetitionEditPage'));
const AdminCompetitionSubmissionsPage = React.lazy(() => import('./pages/admin/AdminCompetitionSubmissionsPage'));
const AdminCompetitionGradingPage = React.lazy(() => import('./pages/admin/AdminCompetitionGradingPage'));
const AdminLivePage = React.lazy(() => import('./pages/admin/AdminLivePage'));
const AdminDiscountCodesPage = React.lazy(() => import('./pages/admin/AdminDiscountCodesPage'));
const AdminCampaignsPage = React.lazy(() => import('./pages/admin/AdminCampaignsPage'));
const AdminCoursesPage = React.lazy(() => import('./pages/admin/AdminCoursesPage'));
const AdminCourseEditPage = React.lazy(() => import('./pages/admin/AdminCourseEditPage'));
const AdminLessonEditPage = React.lazy(() => import('./pages/admin/AdminLessonEditPage'));
const AdminWorkshopsPage = React.lazy(() => import('./pages/admin/AdminWorkshopsPage'));
const AdminWorkshopEditPage = React.lazy(() => import('./pages/admin/AdminWorkshopEditPage'));

// Route Protection Component
const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

// Admin Route Protection Component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-brand-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:outline-none"
      >
        Przejdź do treści
      </a>
      <Header />
      <LiveBanner />
      <CompetitionBanner />
      <main id="main-content" className="flex-1">
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
          </div>
        }>
        <Routes>
          {/* Public Routes */}
          
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="/courses" element={<CoursesPage />} />
          <Route path="/courses/:id" element={<CourseDetailPage />} />
          <Route path="/workshops" element={<WorkshopsPage />} />
          <Route path="/workshops/pay/:token" element={<WorkshopPaymentPage />} />
          <Route path="/workshops/:id" element={<WorkshopDetailPage />} />
          <Route path="/workshops/:id/register" element={<WorkshopRegistrationForm />} />
          <Route path="/competitions" element={<CompetitionsPage />} />
          <Route path="/konkursy" element={<Navigate to="/competitions" replace />} />
          <Route path="/competitions/feedback/:accessToken" element={<CompetitionFeedbackPage />} />
          <Route path="/competitions/:id" element={<Navigate to="/competitions" replace />} />
          <Route path="/competitions/:id/checkout/:submissionId" element={<CompetitionCheckoutPage />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
          <Route path="/newsletter" element={<NewsletterPage />} />
          <Route path="/live" element={<LivePage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          {/* Private Routes */}
          <Route 
            path="/dashboard" 
            element={
              <PrivateRoute>
                <DashboardPage />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/profile" 
            element={
              <PrivateRoute>
                <ProfilePage />
              </PrivateRoute>
            } 
          />
          <Route
            path="/courses/:id/checkout"
            element={
              <PrivateRoute>
                <CheckoutPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/workshops/:id/checkout"
            element={
              <PrivateRoute>
                <WorkshopCheckoutPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/courses/:courseId/lessons"
            element={<LessonPage />}
          />
          <Route
            path="/courses/:courseId/lessons/:lessonId"
            element={<LessonPage />}
          />
          <Route
            path="/courses/:courseId/completed"
            element={
              <PrivateRoute>
                <CourseCompletionPage />
              </PrivateRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboardPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <AdminRoute>
                <AdminAnalyticsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <AdminUsersPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users/:id"
            element={
              <AdminRoute>
                <AdminUserDetailPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/competitions"
            element={
              <AdminRoute>
                <AdminCompetitionsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/competitions/:id"
            element={
              <AdminRoute>
                <AdminCompetitionDetailPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/competitions/:id/edit"
            element={
              <AdminRoute>
                <AdminCompetitionEditPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/competitions/:id/submissions"
            element={
              <AdminRoute>
                <AdminCompetitionSubmissionsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/competitions/:id/grading"
            element={
              <AdminRoute>
                <AdminCompetitionGradingPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/live"
            element={
              <AdminRoute>
                <AdminLivePage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/discount-codes"
            element={
              <AdminRoute>
                <AdminDiscountCodesPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/campaigns"
            element={
              <AdminRoute>
                <AdminCampaignsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/courses"
            element={
              <AdminRoute>
                <AdminCoursesPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/courses/:id/edit"
            element={
              <AdminRoute>
                <AdminCourseEditPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/courses/:courseId/lessons/new"
            element={
              <AdminRoute>
                <AdminLessonEditPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/courses/:courseId/lessons/:lessonId"
            element={
              <AdminRoute>
                <AdminLessonEditPage />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/workshops"
            element={
              <AdminRoute>
                <AdminWorkshopsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/workshops/:id/edit"
            element={
              <AdminRoute>
                <AdminWorkshopEditPage />
              </AdminRoute>
            }
          />

          {/* Catch all route - 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
};

export default App;